import React from 'react';
import { useDeletePost } from '@components/Feed/Post/useDeletePost';
import { ConfirmModal } from '@components/main-components/confirm-modal';
import { useModal } from '@context/modal';
import { useToast } from '@context/toast';
import { AdminDeleteAllUserContentDocument, useMutation } from '@graphql/reactQuery';
import { useAdminDeletePost } from '@hooks/useAdminDeletePost';
import {
  useAdminDeletePostComment,
  useAdminDeletePostCommentReply,
} from '@hooks/useAdminDeletePostComment';

export function DeletePostModal({ postId }: { postId: string }) {
  const { closeModal } = useModal();
  const { deletePost, loading } = useDeletePost({
    postId,
    onDeleteSuccess: () => {
      closeModal();
    },
  });
  return (
    <ConfirmModal
      onConfirm={deletePost}
      onCancel={closeModal}
      title="Delete Post?"
      message="Are you sure you want to delete your post? This action cannot be undone."
      confirmText="Delete"
      cancelText="Cancel"
      destructive
      loading={loading}
    />
  );
}

export function AdminDeletePostModal({ postId }: { postId: string }) {
  const { closeModal } = useModal();
  const { adminDeletePost, loading } = useAdminDeletePost({
    postId,
    onDeleteSuccess: () => {
      closeModal();
    },
  });
  return (
    <ConfirmModal
      onConfirm={adminDeletePost}
      onCancel={closeModal}
      title="Delete Post?"
      message="Are you sure you want to delete this post? This action cannot be undone."
      confirmText="Delete"
      cancelText="Cancel"
      destructive
      loading={loading}
    />
  );
}

export function AdminDeletePostCommentModal({ postCommentId }: { postCommentId: string }) {
  const { closeModal } = useModal();
  const { adminDeletePostComment, loading } = useAdminDeletePostComment({
    postCommentId,
    onDeleteSuccess: () => {
      closeModal();
    },
  });
  return (
    <ConfirmModal
      onConfirm={adminDeletePostComment}
      onCancel={closeModal}
      title="Delete Commment?"
      message="Are you sure you want to delete this comment? This action cannot be undone."
      confirmText="Delete"
      cancelText="Cancel"
      destructive
      loading={loading}
    />
  );
}

export function AdminDeletePostCommentReplyModal({ postCommentId }: { postCommentId: string }) {
  const { closeModal } = useModal();
  const { adminDeletePostCommentReply, loading } = useAdminDeletePostCommentReply({
    postCommentId,
    onDeleteSuccess: () => {
      closeModal();
    },
  });
  return (
    <ConfirmModal
      onConfirm={adminDeletePostCommentReply}
      onCancel={closeModal}
      title="Delete Commment?"
      message="Are you sure you want to delete this comment? This action cannot be undone."
      confirmText="Delete"
      cancelText="Cancel"
      destructive
      loading={loading}
    />
  );
}

export function AdminDeleteAllUserContentModal({ userId }: { userId: string }) {
  const { openToast } = useToast();
  const { closeModal } = useModal();

  const { mutateAsync, isLoading } = useMutation(AdminDeleteAllUserContentDocument, {});

  return (
    <ConfirmModal
      onConfirm={async () => {
        const response = await mutateAsync({ userId });
        if (
          response.data.adminDeleteAllUserContent.__typename ===
          'MutationAdminDeleteAllUserContentSuccess'
        ) {
          openToast({
            text: 'Content deleted successfully',
            variant: 'success',
          });
          closeModal();
        }
      }}
      onCancel={closeModal}
      title="Delete all user content?"
      message="Are you sure you want to delete all user content? This action cannot be undone."
      confirmText="Delete"
      cancelText="Cancel"
      destructive
      loading={isLoading}
    />
  );
}
